/* Angel Number Meaning V2 engine — faithful runtime for the design markup.
   Live adaptations: theme header/footer replace the design's own, the
   contextual preview upgrades from the design's deterministic templates to an
   audited DeepSeek preview from /api/angel-number once the customer commits a
   situation, birth-date selects are server-rendered (Liquid options), and
   checkout runs on the proven shared rail (type "Angel Number", shared_tool
   intents). The paid signals carry the master-preserving core theme so the
   fulfillment family gate always sees "Number" + "Core theme". */
(function () {
  'use strict';
  if (!document.querySelector('[data-an2]')) return;

  var PAGE = '/pages/angel-number-meaning';
  var API_BASE = 'https://reading.deckaura.com/api/angel-number';
  var KEY = 'deckaura.angelnumber.v2';
  var RAIL_TIERS = { message: 'essential', clarity: 'deeper', pattern: 'indepth' };

  /* ------------ design data (verbatim) ------------ */
  var DATA = {
    '000': { word: 'openness', keys: ['Openness', 'Reset', 'Unformed potential'],
      core: '000 is commonly read as open ground: a reset point where the shape of the next chapter has not been decided yet.',
      two: 'In angel-number traditions a zero sequence is treated less as an instruction and more as space, the moment before a direction is chosen.',
      supports: 'Starting from a blank position instead of carrying the last version of the situation forward.',
      overread: 'Openness is not direction. It does not tell you which choice is the correct one.',
      action: 'Name one area you will deliberately leave undecided for two weeks, and one you will decide this week.' },
    '111': { word: 'new alignment', keys: ['Beginnings', 'Focused thought', 'Alignment'],
      core: '111 is usually interpreted as a beginning point: attention, thought and opportunity lining up at the same time.',
      two: 'It is often described as the sequence that reflects what you are already thinking about, rather than introducing something new.',
      supports: 'Naming what you actually want before circumstances decide it for you.',
      overread: 'An opening is not a confirmation. It does not mean a particular outcome has already been settled.',
      action: 'Write the one sentence you would want to be true in three months, then take the smallest step it implies today.' },
    '222': { word: 'balance', keys: ['Balance', 'Partnership', 'Patience'],
      core: '222 is commonly associated with balance between two things: two people, two options, or effort and rest.',
      two: 'It is usually read as a sequence about something still forming, where the useful move is steadiness rather than force.',
      supports: 'Holding steady while a situation is still taking shape.',
      overread: 'Balance is not agreement. It does not mean the other person has reached the same conclusion as you.',
      action: 'Identify the side of this situation you have been over-carrying, and set one boundary that returns it to the other side.' },
    '333': { word: 'expression', keys: ['Expression', 'Creativity', 'Support'],
      core: '333 is often connected with expression and support: saying the thing you have been rehearsing internally.',
      two: 'In most traditions it points at communication that has been delayed rather than at an outcome.',
      supports: 'A conversation you have already written in your head but have not had.',
      overread: 'Readiness to speak is not a promise about how it will be received.',
      action: 'Say one honest sentence you have been holding back, to the person it actually belongs to.' },
    '444': { word: 'stability', keys: ['Protection', 'Stability', 'Strong foundations'],
      core: '444 is commonly associated with creating stability, recognizing support and strengthening what is already proving consistent in your life.',
      two: 'It is usually read as a foundations sequence: the message is about what holds, not about what excites.',
      supports: 'Building on what has already proved reliable over time.',
      overread: 'Stability shows up as consistency across weeks, not as intensity in a single moment.',
      action: 'Name the one thing here that has been dependable for three months, and give it more weight than the newest event.' },
    '555': { word: 'change', keys: ['Change', 'Movement', 'Release'],
      core: '555 is usually read as change already in motion: conditions shifting faster than the plan you made for them.',
      two: 'It is typically interpreted as a transition marker rather than a description of where the transition lands.',
      supports: 'Letting an arrangement end that you have already outgrown.',
      overread: 'Movement in the pattern is not proof that a specific arrival or return is coming.',
      action: 'List what you will keep and what you will release over the next 30 days, then commit to one item on each side.' },
    '666': { word: 'recalibration', keys: ['Recalibration', 'Material focus', 'Realignment'],
      core: 'In angel-number traditions 666 is read as a recalibration signal: attention has drifted heavily toward worry, money or how things look.',
      two: 'It is generally not treated as an ill omen. It is described as an invitation to rebalance where your attention is going.',
      supports: 'Returning your attention to the part of this you can actually influence.',
      overread: 'It is not a warning about a specific event, and it is not a verdict on your choices.',
      action: 'Pick the single worry taking most of your attention and write down the next practical step it needs.' },
    '777': { word: 'alignment', keys: ['Alignment', 'Inner knowing', 'Confirmation'],
      core: '777 is commonly interpreted as alignment: the sense that your own quiet read of a situation is worth trusting.',
      two: 'It is often described as confirmation of direction rather than confirmation of timing.',
      supports: 'Trusting your own assessment before collecting more opinions.',
      overread: 'Confirmation of a direction is not confirmation of a date.',
      action: 'Write down what you already know but have not acted on, then act on one part of it this week.' },
    '888': { word: 'return', keys: ['Cycles', 'Return', 'Flow'],
      core: '888 is often associated with cycles completing and effort returning: abundance read as a cycle rather than a windfall.',
      two: 'It usually points at work already done rather than at something arriving from outside.',
      supports: 'Recognizing where past effort is now paying back.',
      overread: 'A cycle closing does not mean the result arrives without a decision from you.',
      action: 'Look at the commitment that has been running longest and decide whether to renew, renegotiate or end it.' },
    '999': { word: 'completion', keys: ['Completion', 'Closure', 'Threshold'],
      core: '999 is usually connected with completion: a chapter finishing before the next one is visible.',
      two: 'It is read as an ending sequence, which is why it often arrives while things still feel unresolved.',
      supports: 'Closing something properly instead of leaving it half-open.',
      overread: 'An ending in one area is not a prediction about who or what replaces it.',
      action: 'Name what has actually ended, and say it once in past tense.' },
    '1111': { word: 'attention', keys: ['Threshold', 'Awareness', 'Attention'],
      core: '1111 is the most widely reported sequence, usually read as a threshold: what you are paying attention to right now matters more than usual.',
      two: 'Most explanations connect it to the thought you were holding when you noticed it, not to an external event.',
      supports: 'Noticing what your attention returns to when nothing is distracting you.',
      overread: 'A threshold marks attention, not permission. It does not confirm that a specific person or outcome is on the way.',
      action: 'Write down the thought you were holding when you saw 1111, then decide whether you want to keep feeding it.' },
    '1010': { word: 'decision', keys: ['Decision point', 'Aligned action', 'Reset'],
      core: '1010 is generally read as a decision point: a beginning that only opens once something is deliberately chosen.',
      two: 'It combines the beginning of 1 with the open space of 0, which is why it is usually tied to choice rather than chance.',
      supports: 'Making one clear choice instead of keeping several options warm.',
      overread: 'It does not indicate which option is right, only that leaving it open has a cost.',
      action: 'Set a date this week by which you will decide, and tell one person about it.' },
    '1212': { word: 'progress', keys: ['Progress', 'Trust the step', 'Timing'],
      core: '1212 is commonly interpreted as progress in motion: a step already taken that is still waiting on evidence.',
      two: 'It pairs the beginning of 1 with the patience of 2, and is usually read as an in-between sequence.',
      supports: 'Continuing something you started before it has visibly worked.',
      overread: 'Progress is not arrival. It says nothing about how long the middle stage lasts.',
      action: 'Choose the one thing you will keep doing for 30 days without checking for a result every day.' }
  };

  var AREAS = [
    { id: 'love', title: 'Love & relationships', sub: 'A connection, a partner or dating in general', short: 'Love & relationships',
      q1: 'What was happening in your relationship when you noticed {n}?',
      ph: 'e.g. we have been talking again after a quiet month and I cannot tell if it is different this time',
      pvLabel: 'In your relationship context',
      sit: 'Here, {w} is better measured through consistency and reciprocal effort than emotional intensity. The useful question is not whether the connection feels powerful, but whether it is becoming safer and more dependable.',
      open: 'What would need to become more consistent before you trusted this connection?',
      dq: 'What is this number highlighting about my relationship right now?',
      packs: { message: 'What this number is highlighting in your love life now.', clarity: 'The relationship pattern, condition to watch, and your next move.', pattern: 'How multiple signs connect across your relationship journey and personal numerology.' } },
    { id: 'person', title: 'A specific person / no contact', sub: 'Someone particular is on your mind', short: 'A specific person',
      q1: 'What were you wondering about this person when {n} appeared?',
      ph: 'e.g. it has been three weeks of no contact and I keep wondering whether to reach out',
      pvLabel: 'In this specific situation',
      sit: 'Here, {w} describes what you are being asked to notice, not what the other person is thinking. The useful question is which part of this is actually inside your control this week.',
      open: 'If nothing about them changed, what would you still want to decide for yourself?',
      dq: 'What should I understand about this person and this silence right now?',
      packs: { message: 'What this number may be reflecting about the situation.', clarity: 'Separate the sign from uncertainty and identify what is actually within your control.', pattern: 'Map the repeating numbers, the relationship pattern, boundaries, and a 30-day clarity plan.' } },
    { id: 'career', title: 'Career & money', sub: 'Work, an offer, or a financial direction', short: 'Career & money',
      q1: 'What work or financial decision was active in your mind?',
      ph: 'e.g. I have an offer with better pay but less stability and I keep stalling',
      pvLabel: 'In your work context',
      sit: 'Here, {w} is a description of conditions rather than a verdict on the offer. The useful question is which part of the decision is already supported by evidence, and which part is still assumption.',
      open: 'What would you need to see in writing before this stopped feeling like a risk?',
      dq: 'What is this number highlighting about my work and money direction?',
      packs: { message: 'What this number is highlighting about your work or financial direction.', clarity: 'The opportunity, risk, or condition that deserves attention before your next move.', pattern: 'Your full career sign pattern, Life Path connection, and 30-day action map.' } },
    { id: 'change', title: 'Change or a decision', sub: 'A transition you are weighing', short: 'Change or a decision',
      q1: 'What choice or transition are you currently weighing?',
      ph: 'e.g. deciding whether to move cities this year or wait another one',
      pvLabel: 'In this decision',
      sit: 'Here, {w} says more about how to move than whether to move. The useful question is what would have to be true before the next step stops feeling like a gamble.',
      open: 'What is the smallest version of this change you could test first?',
      dq: 'What should I weigh before committing to this change?',
      packs: { message: 'The central theme behind the number.', clarity: 'What supports moving forward, waiting, or reconsidering.', pattern: 'How multiple signs connect to the transition and what to prioritize over the next 30 days.' } },
    { id: 'spirit', title: 'Spiritual growth', sub: 'Your own patterns and inner work', short: 'Spiritual growth',
      q1: 'What have you been trying to understand about yourself lately?',
      ph: 'e.g. I notice I keep abandoning things right before they get real',
      pvLabel: 'In your inner work',
      sit: 'Here, {w} points at a lesson that is repeating rather than a level you have already passed. The useful question is where you already know the answer and have been waiting for permission.',
      open: 'Which pattern have you now recognized often enough to interrupt?',
      dq: 'What pattern in me is this number pointing at right now?',
      packs: { message: 'The lesson asking for your attention.', clarity: 'The deeper pattern, resistance, and practical integration steps.', pattern: 'Your repeating sign pattern, personal numerology connection, and integration plan.' } },
    { id: 'several', title: 'Several numbers / a repeating pattern', sub: 'More than one sequence is appearing', short: 'Several numbers',
      q1: 'Which other numbers are appearing, and where do you notice them?',
      ph: 'e.g. 111 on clocks, 444 on receipts, and 777 twice this week in dreams',
      pvLabel: 'Across the numbers you are seeing',
      sit: 'When several numbers appear together, the theme they share matters more than any single meaning. The useful question is whether they point at one situation from different angles, or at separate areas of your life.',
      open: 'Are these numbers describing one situation, or two that you have been treating as one?',
      dq: 'What do these repeating numbers point at when read together?',
      packs: { message: 'One number read on its own, in one life area.', clarity: 'Your main number read against one exact situation.', pattern: 'Multiple numbers, optional Life Path connection, and a practical 30-day alignment map.' } }
  ];

  var PACKS = [
    { id: 'message', name: 'Personalized Angel Number Message', short: 'Personal Message', price: '$5.99',
      bestFor: 'one quick personal question',
      bullets: ['Your number read inside one life area', 'What it may be highlighting now', 'One caution to keep in mind', 'One grounded next step'],
      words: 'Approximately 450–600 words' },
    { id: 'clarity', name: 'Angel Number Clarity Reading', short: 'Clarity Reading', price: '$9.99',
      bestFor: 'one exact situation or decision',
      bullets: ['Everything in Personal Message', 'The central pattern you may not be seeing', 'What strengthens or weakens the message', 'Three practical next steps', 'One reflection prompt'],
      words: 'Approximately 750–950 words' },
    { id: 'pattern', name: 'Angel Number Pattern & Life Path Reading', short: 'Sign Pattern & Life Path', price: '$16.99',
      bestFor: 'several repeating numbers or a larger life pattern',
      bullets: ['Main number plus up to three additional numbers', 'Optional Life Path connection', 'Dominant and conflicting themes', 'Risks and safeguards', 'A 30-day alignment map', 'A 7-day sign tracker'],
      words: 'Approximately 1,250–1,500 words' }
  ];

  /* Contract clone (lib/angel-number.ts): master-preserving reduction feeds the
     paid signals' core theme; the visible lookup keeps the design's own
     single-digit root. The two layers are deliberately separate. */
  var CORE_TITLES = {
    '0': 'Infinite Potential', '1': 'New Beginnings', '2': 'Balance & Partnership', '3': 'Creative Expression',
    '4': 'Stability & Protection', '5': 'Major Life Changes', '6': 'Home & Harmony', '7': 'Spiritual Awakening',
    '8': 'Abundance & Power', '9': 'Completion & Purpose', '11': 'Master Intuitive', '22': 'Master Builder',
    '33': 'Master Teacher', '111': 'Manifestation Gateway', '222': 'Trust & Alignment', '333': 'Ascended Master Support',
    '444': 'Angelic Protection', '555': 'Transformation Incoming', '666': 'Realign & Rebalance', '777': 'Divine Luck & Alignment',
    '888': 'Infinite Abundance', '999': 'Sacred Completion', '1010': 'Spiritual Acceleration', '1111': 'Universal Alignment Portal',
    '1212': 'Divine Cycle of Growth', '1234': 'Steps of Progress'
  };

  /* ------------ state ------------ */
  var s = {
    mobile: window.innerWidth < 1080,
    num: '', err: '', phase: 'form',
    area: '', q1: '', q2: '',
    pkg: 'clarity', pkgTouched: false, moreOpen: false,
    others: '', where: '', bm: '', bd: '', by: '', patternQ: '',
    drawer: false, faq: 0, previewBusy: false,
    server: null, fetching: false,
    readingId: '', live: ''
  };
  var t1 = 0, t2 = 0;

  /* ------------ helpers ------------ */
  function $(sel) { return document.querySelector(sel); }
  function $all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function track(name, data) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, Object.assign({ page_funnel: 'an2' }, data || {})); } catch (_) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (_) {}
  }
  function visitorId() {
    var key = 'deckaura_visitor_v1';
    try {
      var existing = localStorage.getItem(key);
      if (existing && /^[a-zA-Z0-9_-]{16,96}$/.test(existing)) return existing;
      var bytes = new Uint8Array(18); crypto.getRandomValues(bytes);
      var next = 'v_' + Array.prototype.map.call(bytes, function (x2) { return x2.toString(16).padStart(2, '0'); }).join('');
      localStorage.setItem(key, next);
      return next;
    } catch (_) { return 'v_' + Date.now().toString(16) + Math.random().toString(36).slice(2, 10); }
  }
  function reduced() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function scrollToId(id, offset) {
    var el = document.getElementById(id);
    if (!el) return;
    try { window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - (offset || 84), behavior: reduced() ? 'auto' : 'smooth' }); } catch (_) {}
  }
  function live(msg) {
    s.live = msg;
    var el = $('[data-an2-live]');
    if (el) el.textContent = msg;
  }
  function save() {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        num: s.num, resultReady: hasResult(), area: s.area, q1: s.q1, q2: s.q2, pkg: s.pkgTouched ? s.pkg : '',
        others: s.others, where: s.where, bm: s.bm, bd: s.bd, by: s.by
      }));
    } catch (_) {}
  }
  function digitSum(v) { return String(v).split('').reduce(function (a, c) { return a + Number(c); }, 0); }
  function rootOf(v) { var n = digitSum(v); while (n > 9) n = digitSum(n); return n; }
  function coreKeyFor(number) {
    if (Object.prototype.hasOwnProperty.call(CORE_TITLES, number)) return number;
    var sum = digitSum(number);
    while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33) sum = digitSum(sum);
    return String(sum);
  }
  function lifePathOf() {
    if (!(s.bm && s.bd && s.by)) return 0;
    var n = digitSum(String(Number(s.bm))) + digitSum(String(Number(s.bd))) + digitSum(s.by);
    while (n > 9 && n !== 11 && n !== 22 && n !== 33) n = digitSum(n);
    return n;
  }
  function lookup(num) {
    if (DATA[num]) return Object.assign({}, DATA[num], { derived: false });
    var r = rootOf(num);
    var key = r === 0 ? '000' : String(r).repeat(3);
    return Object.assign({}, DATA[key], { derived: true, root: r, from: key });
  }
  function areaOf(id) {
    for (var i = 0; i < AREAS.length; i += 1) if (AREAS[i].id === id) return AREAS[i];
    return null;
  }
  function packOf(id) {
    for (var i = 0; i < PACKS.length; i += 1) if (PACKS[i].id === id) return PACKS[i];
    return PACKS[1];
  }
  function recommendId() {
    if (!s.area) return 'message';
    if (s.area === 'several' || (s.bm && s.bd && s.by)) return 'pattern';
    return 'clarity';
  }
  function currentPkgId() { return s.pkgTouched ? s.pkg : recommendId(); }
  function parseOthers() {
    var seen = {};
    return String(s.others || '').split(/[^0-9]+/).filter(function (x) {
      if (!/^[0-9]{1,6}$/.test(x) || x === s.num || seen[x]) return false;
      seen[x] = true;
      return true;
    }).slice(0, 3);
  }
  function patternInputReady() { return parseOthers().length > 0 || Boolean(s.bm && s.bd && s.by); }
  function hasResult() { return s.phase === 'result' && !!s.num; }
  function contextQuestionValid() {
    var length = s.q1.trim().length;
    return length === 0 || length >= 12;
  }
  function contextQuestionError() {
    var lang = String(document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
    var messages = {
      en: 'Write at least 12 characters, or clear this field to use the general question.',
      tr: 'En az 12 karakter yazın veya genel soruyu kullanmak için bu alanı boşaltın.',
      de: 'Schreibe mindestens 12 Zeichen oder leere dieses Feld, um die allgemeine Frage zu verwenden.',
      es: 'Escribe al menos 12 caracteres o vacía este campo para usar la pregunta general.',
      pt: 'Escreva pelo menos 12 caracteres ou deixe este campo vazio para usar a pergunta geral.'
    };
    return messages[lang] || messages.en;
  }
  function disableRail() {
    var api = window.DeckauraDeepReading;
    if (api && typeof api.disable === 'function') {
      try { api.disable(); } catch (_) {}
    }
  }
  function invalidateResult() {
    clearTimeout(t1);
    clearTimeout(t2);
    s.phase = 'form';
    s.server = null;
    s.previewBusy = false;
    s.drawer = false;
    s.readingId = '';
    disableRail();
  }
  function updateCheckoutReadiness() {
    var ready = hasResult() && !!s.area && contextQuestionValid();
    $all('[data-an2-act="continue"],[data-an2-act="checkout"],[data-an2-act="sticky"]').forEach(function (button) {
      button.disabled = !ready;
      button.setAttribute('aria-disabled', ready ? 'false' : 'true');
    });
  }

  /* ------------ deterministic + server preview ------------ */
  function previewTexts() {
    var area = areaOf(s.area);
    if (!area || !s.num) return null;
    var entry = lookup(s.num);
    if (s.server && s.server.key === s.num + '|' + s.area + '|' + s.q1.trim()) {
      return { label: s.server.pvLabel || area.pvLabel, situated: s.server.situated, open: s.server.open, source: s.server.source };
    }
    return { label: area.pvLabel, situated: area.sit.replace('{w}', entry.word), open: area.open, source: 'design_tables' };
  }
  function fetchPreview() {
    var q1 = s.q1.trim();
    if (s.fetching || !hasResult() || !s.area || q1.length < 12) return;
    var key = s.num + '|' + s.area + '|' + q1;
    if (s.server && s.server.key === key) return;
    s.fetching = true;
    s.previewBusy = true;
    renderPreview();
    var body = { number: s.num, areaId: s.area, q1: q1.slice(0, 360), q2: s.q2.trim().slice(0, 240), visitorId: visitorId(), readingId: s.readingId };
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ac ? setTimeout(function () { ac.abort(); }, 16000) : 0;
    fetch(API_BASE + '/preview', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body), signal: ac ? ac.signal : undefined
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, data: j }; }); })
      .then(function (res) {
        if (res.ok && res.data && res.data.situated && res.data.open) {
          s.server = { key: key, situated: res.data.situated, open: res.data.open, pvLabel: res.data.pvLabel || '', source: res.data.servedSource || '' };
          track('an2_preview_served', { source: res.data.servedSource || '' });
        } else {
          track('an2_preview_unavailable', { reason: (res.data && res.data.error) || 'error' });
        }
      })
      .catch(function () { track('an2_preview_unavailable', { reason: 'network' }); })
      .finally(function () {
        if (timer) clearTimeout(timer);
        s.fetching = false;
        s.previewBusy = false;
        renderPreview();
        enableRail();
      });
  }

  /* ------------ checkout rail (shared_tool, dream-clone wiring) ------------ */
  function coreTheme() { return CORE_TITLES[coreKeyFor(s.num)] || ''; }
  function railQuestion() {
    var q1 = s.q1.trim();
    if (q1.length >= 12) return q1.slice(0, 360);
    var area = areaOf(s.area);
    return (area && area.dq) || 'What is this number highlighting in my life right now?';
  }
  function railSignals() {
    var entry = lookup(s.num);
    var area = areaOf(s.area);
    var coreKey = coreKeyFor(s.num);
    var out = 'Number: ' + s.num +
      '; Core theme: ' + coreTheme() +
      '; Life area: ' + (area ? area.title : 'General') +
      '; Theme word: ' + entry.word +
      '; Derived core: ' + (coreKey === s.num ? 'direct entry' : 'reduced to ' + coreKey);
    var extras = parseOthers();
    if (extras.length) out += '; Additional numbers: ' + extras.join(', ');
    var lp = lifePathOf();
    if (lp) out += '; Life Path: ' + lp;
    return out;
  }
  function railContext() {
    var entry = lookup(s.num);
    var base = 'Angel Number Meaning V2. ' + railSignals() + '. Free snapshot: ' + entry.core;
    var pv = previewTexts();
    function quoted(label, bodyText, cap) {
      var room = 2900 - base.length - label.length - 2;
      if (room < 40) return '';
      return label + '"' + bodyText.slice(0, Math.min(cap, room)) + '"';
    }
    if (pv) base += quoted(' Free contextual preview already shown: ', pv.situated, 620) || '';
    var q1 = s.q1.trim();
    if (q1) base += quoted(' Customer situation, their own words: ', q1, 700) || '';
    var q2 = s.q2.trim();
    if (q2) base += quoted(' What they want this sign to help them understand: ', q2, 400) || '';
    if (currentPkgId() === 'pattern') {
      if (s.where.trim()) base += quoted(' Where the numbers are noticed: ', s.where.trim(), 160) || '';
      var pq = s.patternQ.trim();
      if (pq) base += quoted(' Pattern question: ', pq, 300) || '';
    }
    return base;
  }
  function detailsProperty() {
    var area = areaOf(s.area);
    var parts = ['Life area: ' + (area ? area.title : 'General') + '.'];
    var q2 = s.q2.trim();
    if (q2) parts.push('Wants to understand: "' + q2.slice(0, 220) + '".');
    if (currentPkgId() === 'pattern') {
      var extras = parseOthers();
      if (extras.length) parts.push('Other numbers: ' + extras.join(', ') + '.');
      if (s.where.trim()) parts.push('Noticed at: ' + s.where.trim().slice(0, 120) + '.');
      var lp = lifePathOf();
      if (lp) parts.push('Life Path: ' + lp + '.');
      if (s.patternQ.trim()) parts.push('Pattern question: "' + s.patternQ.trim().slice(0, 200) + '".');
    }
    var pv = previewTexts();
    if (pv) parts.push('Free preview shown: "' + pv.situated.slice(0, 300) + '"');
    return parts.join(' ').slice(0, 1200);
  }
  function enableRail() {
    var api = window.DeckauraDeepReading;
    if (!hasResult() || !s.area || !contextQuestionValid()) { disableRail(); return; }
    if (!api || typeof api.enable !== 'function') return;
    var pack = packOf(currentPkgId());
    var pv = previewTexts();
    var area = areaOf(s.area);
    try {
      api.enable({
        type: 'Angel Number', teaser: false, resultReady: true,
        checkoutGuard: function () { return hasResult() && !!s.area && contextQuestionValid() && (currentPkgId() !== 'pattern' || patternInputReady()); },
        presentationVariant: 'angel-number-v2',
        canonicalPage: PAGE, visitorId: visitorId(),
        question: railQuestion(), readingId: s.readingId,
        curiosityQuestion: pv ? pv.open : (area ? area.open : ''),
        focus: area ? area.title : 'General',
        fixedFocus: 'Angel V2 - ' + s.area,
        context: railContext(),
        signals: railSignals(),
        cards: railSignals(),
        spread: 'Angel number lookup: number, core theme, life area, situation',
        scope: pack.name + ' for the number ' + s.num + ' read inside the ' + (area ? area.title.toLowerCase() : 'general') + ' area.',
        confidence: 'Angel-number symbolism supports reflection and decision-making; it does not predict fixed outcomes or reveal another person\'s private thoughts.',
        tool: PAGE,
        checkoutProperties: {
          '_Presentation Variant': 'angel-number-v2',
          '_Page Design': 'angel-number-v2',
          '_Reading Title': pack.name,
          '_Details': detailsProperty()
        }
      });
      if (typeof api.setPreviewAuthorization === 'function') api.setPreviewAuthorization({ previewToken: '', transportFallback: false });
      if (typeof api.setCheckoutPending === 'function') api.setCheckoutPending(false);
    } catch (_) {}
  }
  function checkoutStatus(msg, tone) {
    var el = $('[data-an2-checkout-status]');
    if (!el) return;
    if (!msg) { el.style.display = 'none'; return; }
    el.textContent = msg;
    el.style.display = 'block';
    el.style.color = tone === 'warn' ? '#7A5822' : '#8E3B3B';
  }
  function startCheckout(via) {
    if (!hasResult() || !s.area) { disableRail(); return; }
    if (!contextQuestionValid()) {
      renderQ1Meta();
      disableRail();
      scrollToId('context', 84);
      return;
    }
    var pkgId = currentPkgId();
    if (pkgId === 'pattern' && !patternInputReady()) {
      checkoutStatus('For the pattern reading, add at least one other number you keep seeing, or your birth date for the Life Path layer.', 'err');
      var pst = $('[data-an2-pattern-status]');
      if (pst) pst.style.display = 'block';
      s.drawer = false;
      renderDrawer();
      scrollToId('offer', 84);
      track('an2_pattern_gate', {});
      return;
    }
    track('an2_checkout_started', { tier: RAIL_TIERS[pkgId], via: via || 'panel' });
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.checkoutNow !== 'function') {
      checkoutStatus('Checkout is warming up, please tap again in a second.', 'err');
      return;
    }
    enableRail();
    var intendedTier = RAIL_TIERS[pkgId] || 'deeper';
    var intendedReadingId = s.readingId;
    var busyCard = $('[data-an2-pkg="' + pkgId + '"]');
    if (busyCard) busyCard.classList.add('an2-pk-busy');
    var tries = 0;
    (function go() {
      if (!hasResult() || !contextQuestionValid() || s.readingId !== intendedReadingId || currentPkgId() !== pkgId) {
        if (busyCard) busyCard.classList.remove('an2-pk-busy');
        return;
      }
      tries += 1;
      try { api.setTier(intendedTier); } catch (_) {}
      var ready = true;
      try { ready = typeof api.isPriceReady !== 'function' || api.isPriceReady(); } catch (_) {}
      if (!ready && tries < 12) {
        checkoutStatus('Preparing your secure checkout…', 'warn');
        setTimeout(go, 700);
        return;
      }
      if (busyCard) busyCard.classList.remove('an2-pk-busy');
      if (ready) checkoutStatus('');
      else { checkoutStatus('Checkout is warming up, please tap the button again.', 'err'); return; }
      try { api.checkoutNow(); } catch (_) {}
    })();
  }

  /* ------------ renders ------------ */
  function renderHero() {
    var busy = s.phase === 'busy';
    var input = $('[data-an2-num]');
    if (input && input.value !== s.num) input.value = s.num;
    var btnLabel = $('[data-an2-reveal-label]');
    if (btnLabel) btnLabel.textContent = busy ? 'Revealing…' : 'Reveal My Meaning';
    var errBox = $('[data-an2-err]');
    if (errBox) {
      errBox.style.display = s.err ? 'flex' : 'none';
      var et = $('[data-an2-errtext]');
      if (et) et.textContent = s.err;
    }
    $all('[data-an2-chip]').forEach(function (chip) {
      var on = chip.getAttribute('data-num') === s.num;
      chip.style.background = on ? 'rgba(222,194,141,0.34)' : 'rgba(28,32,51,0.02)';
      chip.style.borderColor = on ? 'rgba(222,194,141,0.7)' : 'rgba(28,32,51,0.14)';
      chip.style.color = on ? '#6E4E1C' : '#33374A';
    });
    var plateLabel = $('[data-an2-plate-label]');
    var plateNum = $('[data-an2-plate-num]');
    if (plateLabel) plateLabel.textContent = hasResult() ? 'Your sequence' : 'Most looked up';
    if (plateNum) plateNum.textContent = hasResult() ? s.num : '1111';
    var pre = $('[data-an2-prelookup]');
    if (pre) pre.style.display = (!hasResult() && !busy) ? 'block' : 'none';
    var busyBox = $('[data-an2-busy]');
    if (busyBox) busyBox.style.display = busy ? 'block' : 'none';
  }
  function renderResult() {
    var wrap = $('[data-an2-result]');
    if (!wrap) return;
    wrap.style.display = hasResult() ? 'block' : 'none';
    if (!hasResult()) { renderDrawer(); updateCheckoutReadiness(); return; }
    var entry = lookup(s.num);
    $all('[data-an2-titlenum]').forEach(function (n) { n.textContent = s.num; });
    var big = $('[data-an2-bignum]');
    if (big) big.textContent = s.num;
    var keys = $('[data-an2-keys]');
    if (keys) {
      while (keys.firstChild) keys.removeChild(keys.firstChild);
      entry.keys.forEach(function (k) {
        var li = document.createElement('li');
        li.setAttribute('style', 'padding:7px 14px;border-radius:999px;border:1px solid rgba(222,194,141,0.42);background:rgba(222,194,141,0.18);font-size:14px;color:#6E4E1C');
        li.textContent = k;
        keys.appendChild(li);
      });
    }
    var dWrap = $('[data-an2-derived]');
    if (dWrap) {
      dWrap.style.display = entry.derived ? 'block' : 'none';
      if (entry.derived) {
        var note = s.num + ' → ' + String(s.num).split('').join(' + ') + ' = ' + digitSum(s.num) + ' → ' + entry.root + ' · read through the themes of ' + entry.from;
        var dn = $('[data-an2-derivednote]');
        if (dn) dn.textContent = note;
      }
    }
    var set = function (sel, value) { var el = $(sel); if (el) el.textContent = value; };
    set('[data-an2-core]', entry.core);
    set('[data-an2-coretwo]', entry.two);
    set('[data-an2-supports]', entry.supports);
    set('[data-an2-overread]', entry.overread);
    set('[data-an2-action]', entry.action);
    var sampleNum = $('[data-an2-samplenum]');
    if (sampleNum) sampleNum.textContent = s.num || '444';
    renderSteps();
    renderContext();
    renderPreview();
    renderOffer();
    renderSticky();
  }
  function renderSteps() {
    $all('[data-an2-step]').forEach(function (node) {
      var idx = node.getAttribute('data-an2-step');
      var done = idx === '1' || (idx === '2' && !!s.area) || (idx === '3' && s.pkgTouched);
      var dot = node.querySelector('[data-an2-stepdot]');
      var label = node.querySelector('[data-an2-steplabel]');
      if (dot) {
        dot.textContent = done ? '✓' : idx;
        dot.style.background = done ? '#DEC28D' : 'transparent';
        dot.style.borderColor = done ? '#DEC28D' : 'rgba(28,32,51,0.28)';
        dot.style.color = done ? '#171B2B' : '#5E6270';
      }
      if (label) label.style.color = done ? '#7A5822' : '#5E6270';
    });
  }
  function renderContext() {
    var area = areaOf(s.area);
    $all('[data-an2-area]').forEach(function (card) {
      var on = card.getAttribute('data-an2-area') === s.area;
      card.setAttribute('aria-checked', on ? 'true' : 'false');
      card.style.background = on ? 'rgba(222,194,141,0.30)' : '#FFFDF7';
      card.style.borderColor = on ? 'rgba(222,194,141,0.62)' : 'rgba(28,32,51,0.10)';
      var mark = card.querySelector('[data-an2-area-dot]');
      if (mark) {
        mark.style.borderColor = on ? '#8A6428' : 'rgba(28,32,51,0.28)';
        mark.style.background = on ? '#DEC28D' : 'transparent';
        mark.textContent = on ? '✓' : '';
      }
    });
    var q1wrap = $('[data-an2-q1wrap]');
    if (q1wrap) q1wrap.style.display = area ? 'block' : 'none';
    if (area) {
      var label = $('[data-an2-q1label]');
      if (label) label.textContent = area.q1.replace('{n}', s.num);
      var ta = $('[data-an2-q1]');
      if (ta) {
        ta.placeholder = area.ph;
        if (ta.value !== s.q1) ta.value = s.q1;
      }
      var q2ta = $('[data-an2-q2]');
      if (q2ta && q2ta.value !== s.q2) q2ta.value = s.q2;
      renderQ1Meta();
    }
  }
  function renderQ1Meta() {
    var invalid = !contextQuestionValid();
    var question = $('[data-an2-q1]');
    if (question) {
      question.setAttribute('minlength', '12');
      if (invalid) question.setAttribute('aria-invalid', 'true');
      else question.removeAttribute('aria-invalid');
    }
    var count = $('[data-an2-q1count]');
    if (count) count.textContent = String(s.q1.length) + ' / 360';
    var selQ = $('[data-an2-selq]');
    if (selQ) selQ.textContent = s.q1.trim() ? '“' + s.q1.trim() + '”' : 'Not added yet';
    var status = $('[data-an2-q1status]');
    if (status) {
      status.textContent = invalid ? contextQuestionError() : (s.q1.length > 15 ? 'Saved for this reading' : 'The more specific this is, the more personal the reading');
      if (invalid) status.setAttribute('role', 'alert');
      else status.removeAttribute('role');
    }
    var cta = $('[data-an2-contextcta]');
    if (cta) cta.textContent = s.q1.trim().length > 15 ? 'See what changes for my situation' : 'Continue';
    var note = $('[data-an2-contextnote]');
    if (note) note.textContent = invalid ? '' : s.q1.trim().length > 15
      ? 'Your free preview is ready below — no payment needed to read it.'
      : 'Add a sentence above for a sharper preview, or continue as is.';
    updateCheckoutReadiness();
  }
  function renderPreview() {
    var wrap = $('[data-an2-preview]');
    if (!wrap) return;
    var on = hasResult() && !!s.area;
    wrap.style.display = on ? 'block' : 'none';
    if (!on) return;
    var busyBox = $('[data-an2-pvbusy]');
    var readyBox = $('[data-an2-pvready]');
    if (busyBox) busyBox.style.display = s.previewBusy ? 'flex' : 'none';
    if (readyBox) readyBox.style.display = s.previewBusy ? 'none' : 'block';
    if (s.previewBusy) return;
    var entry = lookup(s.num);
    var pv = previewTexts();
    var gen = $('[data-an2-pvgeneral]');
    if (gen) gen.textContent = entry.core;
    if (pv) {
      var lb = $('[data-an2-pvlabel]');
      if (lb) lb.textContent = pv.label;
      var sit = $('[data-an2-pvsituated]');
      if (sit) sit.textContent = pv.situated;
      var op = $('[data-an2-pvopen]');
      if (op) op.textContent = pv.open;
    }
  }
  function renderOffer() {
    var area = areaOf(s.area);
    var recommend = recommendId();
    var pkgId = currentPkgId();
    var pack = packOf(pkgId);
    var panelLabel = $('[data-an2-panellabel]');
    if (panelLabel) panelLabel.textContent = s.pkgTouched ? 'Your reading' : 'Your best match';
    $all('[data-an2-pkg]').forEach(function (card) {
      var id = card.getAttribute('data-an2-pkg');
      var on = id === pkgId;
      var hidden = s.mobile && !s.moreOpen && id !== recommend && !s.pkgTouched;
      card.style.display = hidden ? 'none' : 'block';
      card.setAttribute('aria-checked', on ? 'true' : 'false');
      card.classList.toggle('an2-pk-sel', on);
      card.style.background = on ? 'rgba(222,194,141,0.28)' : '#FFFFFF';
      card.style.borderColor = on ? 'rgba(222,194,141,0.62)' : 'rgba(28,32,51,0.12)';
      var dot = card.querySelector('[data-an2-pk-dot]');
      if (dot) {
        dot.style.borderColor = on ? '#8A6428' : 'rgba(28,32,51,0.28)';
        dot.style.background = on ? '#DEC28D' : 'transparent';
        dot.textContent = on ? '✓' : '';
      }
      var badge = card.querySelector('[data-an2-pk-badge]');
      if (badge) badge.style.display = (id === recommend && !on) ? 'inline-flex' : 'none';
      var more = card.querySelector('[data-an2-pk-more]');
      if (more) more.style.display = on ? 'block' : 'none';
      var sub = card.querySelector('[data-an2-pk-sub]');
      if (sub && area) sub.textContent = area.packs[id];
    });
    var moreBtn = $('[data-an2-morewrap]');
    if (moreBtn) moreBtn.style.display = (s.mobile && !s.moreOpen && !s.pkgTouched) ? 'block' : 'none';
    var reasonWrap = $('[data-an2-reasonwrap]');
    if (reasonWrap) {
      var showReason = !s.pkgTouched || pkgId === recommend;
      reasonWrap.style.display = showReason ? 'block' : 'none';
      if (showReason) {
        var reasons = {
          message: 'You shared one number without a life area, so the Personal Message keeps the reading focused on what it highlights now.',
          clarity: 'You shared one number and one exact situation, so the Clarity Reading gives you the relevant depth without adding unrelated areas.',
          pattern: area && area.id === 'several'
            ? 'You are noticing more than one number, so the Pattern Reading compares them instead of reading each one alone.'
            : 'You added a birth date, so the Pattern Reading can include the Life Path layer alongside your numbers.'
        };
        var r = $('[data-an2-reason]');
        if (r) r.textContent = reasons[recommend];
      }
    }
    var patternBox = $('[data-an2-patternbox]');
    if (patternBox) patternBox.style.display = pkgId === 'pattern' ? 'block' : 'none';
    var lp = lifePathOf();
    var lpNote = $('[data-an2-lpnote]');
    if (lpNote) lpNote.textContent = lp
      ? 'Life Path ' + lp + ' will be included as a separate layer in your reading.'
      : 'Optional. Used only to calculate the Life Path layer inside this reading.';
    var pst = $('[data-an2-pattern-status]');
    if (pst && (patternInputReady() || pkgId !== 'pattern')) pst.style.display = 'none';
    var cont = $('[data-an2-continuelabel]');
    if (cont) cont.textContent = 'Continue with ' + pack.short + ' — ' + pack.price;
    var lockedLast = $('[data-an2-locked-last]');
    if (lockedLast) lockedLast.textContent = 'Your grounded next step' + (pkgId === 'pattern' ? ', plus a 30-day alignment map' : '');
    var set = function (sel, value) { var el = $(sel); if (el) el.textContent = value; };
    set('[data-an2-selnum]', s.num);
    set('[data-an2-selarea]', area ? area.short : 'Not selected yet');
    set('[data-an2-selq]', s.q1.trim() ? '“' + s.q1.trim() + '”' : 'Not added yet');
    renderDrawer();
    updateCheckoutReadiness();
  }
  function renderDrawer() {
    var overlay = $('[data-an2-overlay]');
    var drawer = $('[data-an2-drawer]');
    var open = s.drawer && hasResult();
    if (overlay) overlay.style.display = open ? 'block' : 'none';
    if (drawer) drawer.style.display = open ? 'block' : 'none';
    if (!open) { renderSticky(); return; }
    var area = areaOf(s.area);
    var pack = packOf(currentPkgId());
    var set = function (sel, value) { var el = $(sel); if (el) el.textContent = value; };
    set('[data-an2-d-num]', s.num);
    set('[data-an2-d-area]', area ? area.short : 'Not selected yet');
    set('[data-an2-d-q]', s.q1.trim() ? '“' + s.q1.trim() + '”' : railQuestion());
    var othersRow = $('[data-an2-d-others-row]');
    if (othersRow) othersRow.style.display = currentPkgId() === 'pattern' ? 'block' : 'none';
    set('[data-an2-d-others]', parseOthers().join(', ') || 'None added');
    set('[data-an2-d-pack]', pack.name);
    set('[data-an2-d-price]', pack.price);
    var cta = $('[data-an2-d-cta]');
    if (cta) cta.textContent = 'Continue Securely — ' + pack.price;
    renderSticky();
  }
  function renderSticky() {
    var bar = $('[data-an2-sticky]');
    if (!bar) return;
    var on = hasResult() && !!s.area && !s.drawer;
    bar.style.display = on ? 'flex' : 'none';
    document.body.classList.toggle('an2-sticky-on', on);
    if (!on) return;
    var area = areaOf(s.area);
    var pack = packOf(currentPkgId());
    var top = $('[data-an2-stickytop]');
    if (top) top.textContent = s.pkgTouched ? 'Your focus: ' + (area ? area.short : '') : 'Recommended for what you entered';
    var main = $('[data-an2-stickymain]');
    if (main) main.textContent = pack.short + ' · ' + pack.price;
    var cta = $('[data-an2-stickycta]');
    if (cta) cta.textContent = s.pkgTouched ? 'Continue' : 'Choose reading';
  }
  function renderFaq() {
    $all('[data-an2-faq]').forEach(function (btn, i) {
      var open = s.faq === i;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      var sign = btn.querySelector('[data-an2-faq-sign]');
      if (sign) sign.style.transform = open ? 'rotate(45deg)' : 'none';
      var body = btn.nextElementSibling;
      if (body) body.style.display = open ? 'block' : 'none';
    });
  }
  function render() {
    renderHero();
    renderResult();
    renderFaq();
  }

  /* ------------ flow ------------ */
  function reveal(value) {
    var raw = String(value === undefined ? s.num : value).replace(/\s/g, '');
    if (!raw) { s.err = 'Enter the number you keep seeing to continue.'; renderHero(); return; }
    if (!/^[0-9]{1,6}$/.test(raw)) { s.err = 'Use digits only, for example 444 or 1111.'; renderHero(); return; }
    s.num = raw;
    s.err = '';
    invalidateResult();
    s.phase = 'busy';
    s.readingId = 'an2-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    s.server = null;
    save();
    live('Looking up the meaning of ' + raw + '.');
    track('an2_lookup', { number: raw });
    render();
    clearTimeout(t1);
    t1 = setTimeout(function () {
      s.phase = 'result';
      live('The general meaning of ' + raw + ' is ready.');
      save();
      render();
      track('an2_result', { number: raw, derived: lookup(raw).derived ? 1 : 0 });
      setTimeout(function () { scrollToId('result', 84); }, 60);
      enableRail();
    }, reduced() ? 0 : 420);
  }
  function pickArea(id) {
    s.area = id;
    if (!s.pkgTouched) s.pkg = id === 'several' ? 'pattern' : 'clarity';
    s.previewBusy = true;
    save();
    track('an2_area', { area: id });
    renderResult();
    clearTimeout(t2);
    t2 = setTimeout(function () {
      s.previewBusy = false;
      live('Your contextual preview is ready.');
      renderPreview();
      enableRail();
    }, reduced() ? 0 : 480);
  }
  function selectPkg(id) {
    s.pkg = id;
    s.pkgTouched = true;
    save();
    track('an2_pkg', { pkg: id });
    renderSteps();
    renderOffer();
    enableRail();
  }
  function contextContinue() {
    if (!contextQuestionValid()) {
      renderQ1Meta();
      disableRail();
      return;
    }
    scrollToId('preview', 84);
    if (s.q1.trim().length >= 12) fetchPreview();
  }

  /* ------------ events ------------ */
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var chip = t.closest('[data-an2-chip],[data-an2-pop],[data-an2-guide]');
    if (chip) { reveal(chip.getAttribute('data-num')); return; }
    var areaCard = t.closest('[data-an2-area]');
    if (areaCard) { pickArea(areaCard.getAttribute('data-an2-area')); return; }
    var faqBtn = t.closest('[data-an2-faq]');
    if (faqBtn) {
      var idx = $all('[data-an2-faq]').indexOf(faqBtn);
      s.faq = s.faq === idx ? -1 : idx;
      track('an2_faq', { index: idx });
      renderFaq();
      return;
    }
    var act = t.closest('[data-an2-act]');
    if (act) {
      var kind = act.getAttribute('data-an2-act');
      if (kind === 'reveal') reveal();
      else if (kind === 'tocontext') scrollToId('context', 84);
      else if (kind === 'tooffer') scrollToId('offer', 84);
      else if (kind === 'tosample') scrollToId('sample', 84);
      else if (kind === 'contextcontinue') contextContinue();
      else if (kind === 'showmore') { s.moreOpen = true; renderOffer(); }
      else if (kind === 'continue') { s.pkgTouched = true; startCheckout('panel'); }
      else if (kind === 'closedrawer') { s.drawer = false; renderDrawer(); }
      else if (kind === 'editdetails') { s.drawer = false; renderDrawer(); setTimeout(function () { scrollToId('context', 84); }, 80); }
      else if (kind === 'checkout') startCheckout('drawer');
      else if (kind === 'sticky') {
        if (s.pkgTouched) startCheckout('sticky');
        else scrollToId('offer', 84);
      }
      return;
    }
    var pkgCard = t.closest('[data-an2-pkg]');
    if (pkgCard && !t.closest('[data-an2-patternbox]')) { selectPkg(pkgCard.getAttribute('data-an2-pkg')); }
  });
  document.addEventListener('keydown', function (ev) {
    var t = ev.target;
    if (!t) return;
    if (ev.key === 'Enter' && t.hasAttribute && t.hasAttribute('data-an2-num')) { ev.preventDefault(); reveal(); return; }
    if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
    if (!t.closest) return;
    var radio = t.closest('[data-an2-area],[data-an2-pkg]');
    if (radio && t === radio) {
      ev.preventDefault();
      if (radio.hasAttribute('data-an2-area')) pickArea(radio.getAttribute('data-an2-area'));
      else selectPkg(radio.getAttribute('data-an2-pkg'));
    }
  });
  document.addEventListener('input', function (ev) {
    var t = ev.target;
    if (!t || !t.hasAttribute) return;
    if (t.hasAttribute('data-an2-num')) {
      var nextNumber = t.value.replace(/[^0-9]/g, '').slice(0, 6);
      if (t.value !== nextNumber) t.value = nextNumber;
      if (nextNumber !== s.num) {
        s.num = nextNumber;
        s.err = '';
        invalidateResult();
        save();
        render();
      } else if (s.err) { s.err = ''; renderHero(); }
      return;
    }
    if (t.hasAttribute('data-an2-q1')) { s.q1 = t.value.slice(0, 360); save(); renderQ1Meta(); enableRail(); return; }
    if (t.hasAttribute('data-an2-q2')) { s.q2 = t.value.slice(0, 240); save(); return; }
    if (t.hasAttribute('data-an2-others')) { s.others = t.value.slice(0, 40); save(); renderOffer(); return; }
    if (t.hasAttribute('data-an2-where')) { s.where = t.value.slice(0, 80); save(); return; }
    if (t.hasAttribute('data-an2-patternq')) { s.patternQ = t.value.slice(0, 240); return; }
  });
  document.addEventListener('change', function (ev) {
    var t = ev.target;
    if (!t || !t.hasAttribute) return;
    if (t.hasAttribute('data-an2-bm')) { s.bm = t.value; save(); renderOffer(); enableRail(); return; }
    if (t.hasAttribute('data-an2-bd')) { s.bd = t.value; save(); renderOffer(); enableRail(); return; }
    if (t.hasAttribute('data-an2-by')) { s.by = t.value; save(); renderOffer(); enableRail(); return; }
  });
  window.addEventListener('resize', function () {
    var m = window.innerWidth < 1080;
    if (m !== s.mobile) { s.mobile = m; renderOffer(); }
  });

  /* ------------ init ------------ */
  try {
    var saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (saved && /^[0-9]{1,6}$/.test(String(saved.num || ''))) {
      s.num = String(saved.num);
      s.area = areaOf(saved.area) ? saved.area : '';
      s.q1 = String(saved.q1 || '').slice(0, 360);
      s.q2 = String(saved.q2 || '').slice(0, 240);
      if (saved.pkg && packOf(saved.pkg).id === saved.pkg) { s.pkg = saved.pkg; s.pkgTouched = true; }
      s.others = String(saved.others || '').slice(0, 40);
      s.where = String(saved.where || '').slice(0, 80);
      s.bm = String(saved.bm || ''); s.bd = String(saved.bd || ''); s.by = String(saved.by || '');
      s.phase = saved.resultReady === false ? 'form' : 'result';
      s.readingId = 'an2-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }
  } catch (_) {}
  render();
  if (hasResult()) enableRail();
})();
